import { useSyncExternalStore } from 'react';

/**
 * Histórico dos avisos (o que tocou som ou apareceu como notificação), para o operador saber de
 * onde veio o bipe. Fica neste navegador, por usuário: os avisos só existem com a tela aberta.
 */

export type NotificationKind = 'queue' | 'message' | 'transfer' | 'mention' | 'sync' | 'channel' | 'call' | 'help' | 'mood';

export interface NotificationEntry {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  attendanceId: string | null;
  at: string;
  read: boolean;
  /** Avisos repetidos do mesmo atendimento (ex.: 3 mensagens seguidas) viram uma linha só. */
  count: number;
}

const MAX_ENTRIES = 100;
const MAX_AGE_MS = 7 * 24 * 3600_000;

let userId: string | null = null;
let entries: NotificationEntry[] = [];
const listeners = new Set<() => void>();

const key = () => `atd.notifications.${userId}`;

function persist(): void {
  if (!userId) return;
  try {
    localStorage.setItem(key(), JSON.stringify(entries));
  } catch {
    // sem armazenamento (aba anônima, cota): fica só em memória
  }
}

function emit(): void {
  persist();
  listeners.forEach((l) => l());
}

/** Troca de usuário (login/logout): carrega o histórico dele. */
export function loadNotificationLog(id: string | null): void {
  if (id === userId) return;
  userId = id;
  entries = [];
  if (id) {
    try {
      const raw = JSON.parse(localStorage.getItem(key()) ?? '[]') as NotificationEntry[];
      const minAt = Date.now() - MAX_AGE_MS;
      entries = Array.isArray(raw) ? raw.filter((e) => Date.parse(e.at) >= minAt).slice(0, MAX_ENTRIES) : [];
    } catch {
      entries = [];
    }
  }
  listeners.forEach((l) => l());
}

/** Registra um aviso. Mensagem ou fila do mesmo atendimento ainda não lida soma na linha existente. */
export function logNotification(e: { kind: NotificationKind; title: string; body: string; attendanceId?: string | null }): void {
  const attendanceId = e.attendanceId ?? null;
  const now = new Date().toISOString();
  // Várias abas recebem o mesmo aviso (e compartilham o histórico): ignora o repetido em até 3 s.
  const dup = entries.find(
    (x) => x.kind === e.kind && x.attendanceId === attendanceId && x.title === e.title && x.body === e.body && Date.now() - Date.parse(x.at) < 3000,
  );
  if (dup) return;
  const same = attendanceId && (e.kind === 'message' || e.kind === 'queue') ? entries.find((x) => !x.read && x.kind === e.kind && x.attendanceId === attendanceId) : undefined;
  if (same) {
    entries = [{ ...same, title: e.title, body: e.body, at: now, count: same.count + 1 }, ...entries.filter((x) => x !== same)];
  } else {
    const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    entries = [{ id, kind: e.kind, title: e.title, body: e.body, attendanceId, at: now, read: false, count: 1 }, ...entries].slice(0, MAX_ENTRIES);
  }
  emit();
}

export function markAllRead(): void {
  if (!entries.some((e) => !e.read)) return;
  entries = entries.map((e) => (e.read ? e : { ...e, read: true }));
  emit();
}

/** Abriu o atendimento: os avisos dele estão vistos. */
export function markAttendanceRead(attendanceId: string): void {
  if (!entries.some((e) => !e.read && e.attendanceId === attendanceId)) return;
  entries = entries.map((e) => (e.attendanceId === attendanceId && !e.read ? { ...e, read: true } : e));
  emit();
}

export function clearNotificationLog(): void {
  entries = [];
  emit();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  // Outra aba do mesmo usuário registrou ou leu avisos.
  const onStorage = (ev: StorageEvent) => {
    if (ev.key !== key() || !userId) return;
    try {
      entries = JSON.parse(ev.newValue ?? '[]') as NotificationEntry[];
    } catch {
      entries = [];
    }
    l();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(l);
    window.removeEventListener('storage', onStorage);
  };
}

export function useNotificationLog(): NotificationEntry[] {
  return useSyncExternalStore(subscribe, () => entries);
}
